import React, { useState } from 'react';
import WaButton from '../../components/wa/WaButton';
import { Card, SectionHead } from './customerUi';
import { getMyFamilyCalendarFeed } from '../../services/calendarFeedService';

/* "Sync to your calendar" — the family's own subscribable feed (2026-08-19).
 *
 * One card, mounted at the bottom of BOTH My Lessons and the Season Calendar:
 * the same standing subscription answers "when is my lesson" and "when is the
 * recital", so it lives wherever either question is being asked. The per-recital
 * "Add to calendar" buttons above it on the Season Calendar stay — a one-tap
 * single-event add is a different (lower-commitment) action than subscribing.
 *
 * The link is minted LAZILY, on the first press — not on page load — so the
 * page costs no callable for the majority who never subscribe. What the feed
 * carries is decided entirely server-side by the token's scope: this family's
 * lessons and self-assigned make-ups, rehearsals/group classes their students
 * are cast in, every recital, and the season's closures. Never trials, never
 * anything staff-only.
 *
 * The URL is a private credential (it reveals this family's schedule) — the
 * copy says so plainly, and "Get a new link" is the recovery path: it silently
 * disconnects anything subscribed to the old one, which is the point.
 */

const linkRow = { display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginTop: 10 };
const hint = {
	fontSize: '0.8rem',
	color: 'var(--text-muted, #7b8499)',
	lineHeight: 1.55,
	margin: '10px 0 0',
};

const CalendarSyncCard = () => {
	const [feed, setFeed] = useState(null);
	const [busy, setBusy] = useState(false);
	const [error, setError] = useState('');
	const [copied, setCopied] = useState(false);

	const load = async (opts) => {
		setBusy(true);
		setError('');
		try {
			setFeed(await getMyFamilyCalendarFeed(opts));
		} catch (e) {
			console.error('Error getting family calendar feed:', e);
			setError('Couldn’t get your calendar link. Please try again.');
		} finally {
			setBusy(false);
		}
	};

	const copyUrl = async () => {
		try {
			await navigator.clipboard.writeText(feed.httpsUrl);
			setCopied(true);
			setTimeout(() => setCopied(false), 2500);
		} catch {
			// Clipboard can be denied — the URL is still selectable below.
		}
	};

	const rotate = () => {
		if (
			// eslint-disable-next-line no-alert
			window.confirm(
				'Get a new link? Any calendar already subscribed with the old link will stop updating and will need the new one.'
			)
		) {
			load({ rotate: true });
		}
	};

	return (
		<Card style={{ marginTop: '1rem' }}>
			<SectionHead icon='fa-calendar-arrow-down' label='Sync to your calendar' color='#82c6c6' />
			<div style={{ padding: '0 15px 14px' }}>
			<p style={{ ...hint, marginTop: 0 }}>
				Subscribe once and your lessons, make-ups, rehearsals, recitals and studio
				closures stay up to date in your own calendar app — nothing to re-add when
				something changes.
			</p>

			{!feed && (
				<div style={linkRow}>
					<WaButton appearance='outlined' loading={busy} disabled={busy} onClick={() => load()}>
						Get my calendar link
					</WaButton>
				</div>
			)}

			{feed && (
				<>
					<div style={linkRow}>
						<WaButton
							appearance='outlined'
							onClick={() => {
								window.location.href = feed.webcalUrl;
							}}>
							<i className='fa-sharp fa-solid fa-calendar-circle-plus' style={{ marginRight: 6 }} aria-hidden='true' />
							Subscribe (Apple / phone)
						</WaButton>
						<WaButton appearance='outlined' onClick={copyUrl}>
							<i
								className={`fa-sharp fa-solid ${copied ? 'fa-check' : 'fa-copy'}`}
								style={{ marginRight: 6 }}
								aria-hidden='true'
							/>
							{copied ? 'Copied' : 'Copy link for Google Calendar'}
						</WaButton>
					</div>
					<p style={hint}>
						<strong>Apple / iPhone:</strong> “Subscribe” opens your calendar app directly.{' '}
						<strong>Google Calendar:</strong> copy the link, then in Google Calendar choose{' '}
						<em>Other calendars → “+” → From URL</em> and paste it. Google refreshes
						subscribed calendars on its own schedule, so changes can take a few hours to
						show there — the portal is always current.
					</p>
					<p style={hint}>
						This link is private to your family — anyone with it can see your schedule.
						If it ever ends up somewhere it shouldn’t,{' '}
						<button
							type='button'
							onClick={rotate}
							disabled={busy}
							style={{
								appearance: 'none',
								border: 'none',
								background: 'transparent',
								padding: 0,
								font: 'inherit',
								color: 'var(--text-heading, #384971)',
								fontWeight: 600,
								textDecoration: 'underline',
								cursor: 'pointer',
							}}>
							get a new link
						</button>{' '}
						— the old one stops working immediately.
					</p>
				</>
			)}

			{error && (
				<p style={{ ...hint, color: 'var(--danger, #b3402a)' }} role='alert'>
					{error}
				</p>
			)}
			</div>
		</Card>
	);
};

export default CalendarSyncCard;
